import React from 'react';
import s from './SidebarEnvironment.module.scss';
import { Theme } from '../ChatWidget/types';
import cs from 'classnames';
import { Text, Badge, Tooltip, Heading, IconButton } from '@wix/design-system';
import { EraseSmall, DismissSmall } from '@wix/wix-ui-icons-common';
import {
  AnimatedBackground,
  AnimatedBackgroundRef,
} from '../AnimatedBackground/AnimatedBackground/AnimatedBackground';

type SidebarEnvironmentProps = {
  children: React.ReactNode;
  title: string;
  theme?: Theme;
  isFooterVisible: boolean;
  onClean: () => void;
  onClose: () => void;
};

const themes = {
  original: s.themeOriginal,
  light: s.themeLight,
  dark: s.themeDark,
};

export const SidebarEnvironment: React.FC<SidebarEnvironmentProps> = ({
  children,
  theme = 'light',
  isFooterVisible = true,
  title,
  onClean,
  onClose,
}) => {
  const animatedBackgroundRef = React.useRef<AnimatedBackgroundRef>(null);

  return (
    <div className={cs(s.sidebar, themes[theme])}>
      <div className={s.bg}>
        <AnimatedBackground
          theme={theme === 'dark' ? 'dark' : 'light'}
          isIdle
          ref={animatedBackgroundRef}
          onReady={() => {
            animatedBackgroundRef.current?.impulseFromCenter();
          }}
        />
      </div>

      <div className={s.content}>
        <div className={s.header}>
          <div className={s.headerLeft}>
            <div className={s.title}>
              <Heading size="medium">{title}</Heading>
            </div>
            <Badge size="tiny" skin="neutralStandard" className={s.betaBadge}>
              BETA
            </Badge>
          </div>
          <div className={s.headerRight}>
            <div className={s.cleanButton}>
              <Tooltip inline content="Clear Chat">
                <IconButton
                  size="tiny"
                  skin={theme === 'dark' ? 'transparent' : 'light'}
                  priority="secondary"
                  onClick={onClean}
                >
                  <EraseSmall />
                </IconButton>
              </Tooltip>
            </div>
            <div className={s.closeButton}>
              <IconButton
                size="tiny"
                skin={theme === 'dark' ? 'transparent' : 'light'}
                priority="secondary"
                onClick={onClose}
              >
                <DismissSmall />
              </IconButton>
            </div>
          </div>
        </div>

        <div className={s.chat}>{children}</div>

        <div className={cs(s.footer, { [s.footerHidden]: !isFooterVisible })}>
          <div className={s.footerInner}>
            <div className={s.footerContent}>
              <Text size="tiny">
                AI can make mistakes, so double-check that the results are
                accurate before using them
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
